/* eslint-disable react/jsx-no-target-blank */
import React from 'react'
import { useIntl } from 'react-intl'
import {
  FaTachometerAlt,
  FaUsers,
  FaTags,
  FaBoxes,
  FaMoneyBillAlt,
  FaBuilding,
  FaCode,
  FaGlobe,
  FaLanguage,
  FaCoins,
  FaAd,
  FaCogs,
  FaCreditCard, // Added for Payment Methods
  FaTruck, // Added for Suppliers
  FaShoppingCart, // Added for Purchased Products
  FaDollarSign, // Added for Payments
  FaTools,
  FaUserFriends,
  FaPercentage,
} from 'react-icons/fa'
import { AsideMenuItemWithSub } from './AsideMenuItemWithSub'
import { AsideMenuItem } from './AsideMenuItem'
import { useAuth } from '../../../../app/modules/auth'

// Helper function to check permissions
const hasAnyPermission = (permissions: string[], requiredPermissions: string[]) => {
  return requiredPermissions.some((permission) => permissions.includes(permission))
}

export function AsideMenuMain() {
  const intl = useIntl()
  const { auth } = useAuth()
  const userPermissions = auth?.permissions || []

  return (
    <>
      {/* Dashboard */}
      {hasAnyPermission(userPermissions, ['view-dashboard']) && (
        <AsideMenuItem
          to='/dashboard'
          icon={<FaTachometerAlt />}
          title={intl.formatMessage({ id: 'MENU.DASHBOARD' })}
        />
      )}

      {/* Company & Services Group */}
      {hasAnyPermission(userPermissions, [
        'manage-companies',
        'manage-company-codes',
        'manage-service-categories',
        'manage-services',
        'manage-bundles',
      ]) && (
          <AsideMenuItemWithSub
            to='#'
            title={intl.formatMessage({
              id: 'MENU.COMPANY_SERVICES',
              defaultMessage: 'Company & Services',
            })}
            icon={<FaBuilding />}
          >
            {userPermissions.includes('manage-companies') && (
              <AsideMenuItem
                to='/admin/pages/companies'
                icon={<FaBuilding />}
                title={intl.formatMessage({
                  id: 'MENU.COMPANY',
                  defaultMessage: 'Company',
                })}
              />
            )}
            {userPermissions.includes('manage-company-codes') && (
              <AsideMenuItem
                to='/admin/pages/company_codes'
                icon={<FaCode />}
                title={intl.formatMessage({
                  id: 'MENU.COMPANYCODE',
                  defaultMessage: 'Company Code',
                })}
              />
            )}
            {userPermissions.includes('manage-service-categories') && (
              <AsideMenuItem
                to='/admin/pages/service_category'
                icon={<FaTags />}
                title={intl.formatMessage({
                  id: 'MENU.SERVICECATEGORY',
                  defaultMessage: 'Service Category',
                })}
              />
            )}
            {userPermissions.includes('manage-services') && (
              <AsideMenuItem
                to='/admin/pages/services'
                icon={<FaBoxes />}
                title={intl.formatMessage({
                  id: 'MENU.SERVICE',
                  defaultMessage: 'Service',
                })}
              />
            )}
            {userPermissions.includes('manage-bundles') && (
              <AsideMenuItem
                to='/admin/pages/bundles'
                icon={<FaBoxes />}
                title={intl.formatMessage({
                  id: 'MENU.BUNDLE',
                  defaultMessage: 'Bundle',
                })}
              />
            )}
          </AsideMenuItemWithSub>
        )}

      {/* Financials Group */}
      {hasAnyPermission(userPermissions, [
        'manage-transactions',
        'manage-purchased-products',
        'manage-payments',
        'manage-balances',
      ]) && (
          <AsideMenuItemWithSub
            to='#'
            title={intl.formatMessage({
              id: 'MENU.FINANCIALS',
              defaultMessage: 'Financials',
            })}
            icon={<FaMoneyBillAlt />}
          >
            {userPermissions.includes('manage-transactions') && (
              <AsideMenuItem
                to='/admin/pages/transactions'
                icon={<FaMoneyBillAlt />}
                title={intl.formatMessage({
                  id: 'MENU.MONEYTRANSACTIONS',
                  defaultMessage: 'Transactions',
                })}
              />
            )}
            {userPermissions.includes('manage-purchased-products') && (
              <AsideMenuItem
                to='/admin/pages/purchased-products'
                icon={<FaShoppingCart />}
                title={intl.formatMessage({
                  id: 'MENU.PURCHASEDPRODUCTS',
                  defaultMessage: 'Purchased Products',
                })}
              />
            )}
            {userPermissions.includes('manage-payments') && (
              <AsideMenuItem
                to='/admin/pages/payments'
                icon={<FaDollarSign />}
                title={intl.formatMessage({
                  id: 'MENU.PAYMENTS',
                  defaultMessage: 'Payments',
                })}
              />
            )}
            {userPermissions.includes('manage-balances') && (
              <AsideMenuItem
                to='/admin/pages/balances'
                icon={<FaDollarSign />}
                title={intl.formatMessage({
                  id: 'MENU.BALANCE',
                  defaultMessage: 'Balance',
                })}
              />
            )}
          </AsideMenuItemWithSub>
        )}

      {/* Geographical Group */}
      {hasAnyPermission(userPermissions, [
        'manage-countries',
        'manage-provinces',
        'manage-districts',
      ]) && (
          <AsideMenuItemWithSub
            to='#'
            title={intl.formatMessage({
              id: 'MENU.GEOGRAPHICAL',
              defaultMessage: 'Geographical',
            })}
            icon={<FaGlobe />}
          >
            {userPermissions.includes('manage-countries') && (
              <AsideMenuItem
                to='/admin/pages/countries'
                icon={<FaGlobe />}
                title={intl.formatMessage({
                  id: 'MENU.COUNTRY',
                  defaultMessage: 'Country',
                })}
              />
            )}
            {userPermissions.includes('manage-provinces') && (
              <AsideMenuItem
                to='/admin/pages/provinces'
                icon={<FaGlobe />}
                title={intl.formatMessage({
                  id: 'MENU.PROVINCE',
                  defaultMessage: 'Province',
                })}
              />
            )}
            {userPermissions.includes('manage-districts') && (
              <AsideMenuItem
                to='/admin/pages/districts'
                icon={<FaGlobe />}
                title={intl.formatMessage({
                  id: 'MENU.DISTRICT',
                  defaultMessage: 'District',
                })}
              />
            )}
          </AsideMenuItemWithSub>
        )}

      {/* Others */}
      {userPermissions.includes('manage-resellers') && (
        <AsideMenuItem
          to='/admin/pages/resellers'
          icon={<FaUsers />}
          title={intl.formatMessage({ id: 'MENU.RESELLER' })}
        />
      )}
      {userPermissions.includes('manage-orders') && (
        <AsideMenuItem
          to='/admin/pages/orders'
          icon={<FaBoxes />}
          title={intl.formatMessage({ id: 'MENU.ORDER' })}
        />
      )}


{/* <AsideMenuItemWithSub
  to='#'
  title={intl.formatMessage({
    id: 'MENU.HAWALA_MANAGEMENT',
    defaultMessage: 'مدیریت حواله',
  })}
  icon={<FaMoneyBillAlt />}
>
  <AsideMenuItem
    to='/admin/pages/hawala-branches'
    icon={<FaBuilding />}
    title={intl.formatMessage({
      id: 'MENU.HAWALA_BRANCHES',
      defaultMessage: 'برنچ های حواله',
    })}
  />
  <AsideMenuItem
    to='/admin/pages/hawalas'
    icon={<FaBoxes />}
    title={intl.formatMessage({
      id: 'MENU.HAWALA_ORDERS',
      defaultMessage: 'درخواست های حواله',
    })}
  />
</AsideMenuItemWithSub> */}




      {userPermissions.includes('manage-languages') && (
        <AsideMenuItem
          to='/admin/pages/languages'
          icon={<FaLanguage />}
          title={intl.formatMessage({ id: 'MENU.LANGUAGE' })}
        />
      )}
      {userPermissions.includes('manage-currencies') && (
        <AsideMenuItem
          to='/admin/pages/currencies'
          icon={<FaCoins />}
          title={intl.formatMessage({ id: 'MENU.CURRENCY' })}
        />
      )}
      {userPermissions.includes('manage-advertisements') && (
        <AsideMenuItem
          to='/admin/pages/advertisements'
          icon={<FaAd />}
          title={intl.formatMessage({ id: 'MENU.ADVERTISEMENT' })}
        />
      )}

      {/* People Settings */}
      {hasAnyPermission(userPermissions, ['manage-users', 'manage-suppliers']) && (
        <AsideMenuItemWithSub
          to='#'
          title={intl.formatMessage({
            id: 'MENU.PEOPLE',
            defaultMessage: 'کاربران و افراد',
          })}
          icon={<FaTools />}
        >
          {userPermissions.includes('manage-users') && (
            <AsideMenuItem
              to='/admin/pages/users'
              icon={<FaCogs />}
              title={intl.formatMessage({
                id: 'MENU.USERS',
                defaultMessage: 'کاربران و کارمندان',
              })}
            />
          )}
          {userPermissions.includes('manage-suppliers') && (
            <AsideMenuItem
              to='/admin/pages/suppliers'
              icon={<FaTruck />}
              title={intl.formatMessage({
                id: 'MENU.SUPPLIERS',
                defaultMessage: 'تامین کننده گان',
              })}
            />
          )}
        </AsideMenuItemWithSub>
      )}

      {/* General Settings */}
      {hasAnyPermission(userPermissions, [
        'manage-app-settings',
        'manage-roles',
        'manage-payment-methods',
      ]) && (
          <AsideMenuItemWithSub
            to='#'
            title={intl.formatMessage({
              id: 'MENU.GENERALSETTINGS',
              defaultMessage: 'تنظیمات عمومی',
            })}
            icon={<FaTools />}
          >
            {userPermissions.includes('manage-app-settings') && (
              <AsideMenuItem
                to='/admin/pages/app_setting'
                icon={<FaCogs />}
                title={intl.formatMessage({
                  id: 'MENU.TELEGRAMSETTING',
                  defaultMessage: 'تنظیمات تیلیگرام',
                })}
              />
            )}
            {userPermissions.includes('manage-roles') && (
              <AsideMenuItem
                to='/admin/pages/roles-permissions'
                icon={<FaUsers />}
                title={intl.formatMessage({
                  id: 'MENU.ROLEPERMISSION',
                  defaultMessage: 'نقش و صلاحیت ها',
                })}
              />
            )}
            {userPermissions.includes('manage-payment-methods') && (
              <AsideMenuItem
                to='/admin/pages/payment-methods'
                icon={<FaCreditCard />}
                title={intl.formatMessage({
                  id: 'MENU.PAYMENTMETHOD',
                  defaultMessage: 'Payment Methods',
                })}
              />
            )}
            {userPermissions.includes('manage-reseller-groups') && (
              <AsideMenuItem
                to='/admin/pages/reseller-groups'
                icon={<FaUserFriends />}
                title={intl.formatMessage({
                  id: 'MENU.RESELLERGROUPS',
                  defaultMessage: 'گروپ فروشنده گان',
                })}
              />
            )}
            {userPermissions.includes('manage-group-discounts') && (
              <AsideMenuItem
                to='/admin/pages/group-discounts'
                icon={<FaPercentage />}
                title={intl.formatMessage({
                  id: 'MENU.RESELLERGROUPS',
                  defaultMessage: 'تنظیمات تخفیف ها',
                })}
              />
            )}
              {userPermissions.includes('manage-group-discounts') && (
              <AsideMenuItem
                to='/admin/pages/group-pricings'
                icon={<FaMoneyBillAlt />}
                title={intl.formatMessage({
                  id: 'MENU.RESELLERDISCOUNTSS',
                  defaultMessage: 'تنظیمات قیمت های کریدیت',
                })}
              />
            )}
          </AsideMenuItemWithSub>

        )}

    </>
  )
}
